import { createSlice } from '@reduxjs/toolkit';
import { getAffinityBySegment, 
    getBrandedUnbrandedCounts,
    getDigitalVsNonDigitalAffinity, 
    getDistributionExperience, 
    getDynamicTopicCounts, 
    getPeerPeerAffinityData, 
    getPersonalPromotionChannelAffinity, 
    getSpecialtiesData, 
    getTopStatesByIndividuals, 
    getYearsOfExperience, 
    getContentAffinityKeywords,
    getVendorAffinity,
    getTotalPhysicians, 
    getDigitalChannelAffinity,
    getRegionCount } from '../thunks/physicanTargetListAnalysisThunk';
import { toast } from 'react-toastify';
import { replaceKeyWithValue,
    createTreeMapSeries, 
    createTopicData, 
    createPeerToPeerAffinityData, 
    createContentTypeData, 
    createDistributionYearExp, 
    createMapSeries, 
    createAffinityBySegmentData, 
    createDigitalVsNonDigitalData,
    generateAffinityChartSeries,
    createSankeyChartSeries,
    transformChartSeriesForSankey,
    removeNotFound } from '../../utility';
import { LEVELS, TOPICS, PREFERENCE, hcpCountTooltipCompanies } from '../../constants';

const canShowCounts = (action) => hcpCountTooltipCompanies.includes(Number(action.meta?.arg?.companyId));

const physicianTargetListAnalysisSlice = createSlice({
    name: 'physicianTargetListAnalysis',
    initialState: {
        isLoading: false,
        error: null,
        totalPhysicians: 0,
        matchRate: 0,
        yearofExp: {
            chartSeries: [],
            isLoading: false,
            error: null
        },
        specialties: {
            chartSeries: [],
            isLoading: false,
            error: null
        },
        distributionByLocation: {
            isLoading: false,
            error: null,
            usMapChartSeries: []
            
        },
        digitalVsNonDigital: {
            isLoading: false,
            error: null,
            chartSeries: []
        },
        digitalChannel: {
            isLoading: false,
            error: null,
            data: null,
            selectedPreference: 'All',
            chartSeries: [],
            categories: []
        },
        affinityBySegment: {
            selectedSegment: '',
            digitalAffinityChartData: {
                chartSeries: [],
                categories: []
            },
            PeerToPeerAffinityChartData: {
                chartSeries: [],
                categories: []
            },
            PersonalPromotionAffinityChartData: {
                chartSeries: [],
                categories: []
            },
            isLoading: false,
            error: null
        },
        personalPromotionChannel: {
            isLoading: false,
            error: null,
            data: null,
            selectedPreference: 'All',
            chartSeries: [],
            categories: []
        },
        peerToPeer: {
            isLoading: false,
            error: null,
            virtualChannel: {
                labels: [],
                chartSeries: []
            },
            peerToPeerChannel: {
                labels: [],
                chartSeries: []
            },
            inPersonChannel: {
                labels: [],
                chartSeries: []
            }
        },
        vendorAffinity: {
            isLoading: false,
            error: null,
            data: null,
            chartSeries: [],
            categories: [],
            selectedChannel: '',
            vendors: []
        },
        contentAffinity: {
            isLoading: false,
            error: null,
            data: null,
            chartSeries: [],
            totalKeywordsCount: 0
        },
        topic: {
            isLoading: false,
            error: null,
            chartSeries: [],
            topRatedCount: 0,
            topRatedTopic: ''
        },
        contentType: {
            isLoading: false,
            error: null,
            chartSeries: [],
            branded: 0,
            unbranded: 0
        },
        distributionByYearsofExp: {
            isLoading: false,
            error: null,
            chartSeries: []
        },
        topStatesByIndividuals: {
            isLoading: false,
            error: null,
            chartSeries: [],
            mapSeries: [],
            overAllIndividuals: 0,
            top15Total: 0
        }
    },
    reducers: {
        setDigitalChannelPreference: (state, action) => {
            state.digitalChannel.selectedPreference = action.payload.preference;
            const preference = PREFERENCE[action.payload.preference];
            state.digitalChannel.chartSeries = state.digitalChannel.data[preference]?.seriesData || [];
            state.digitalChannel.categories = state.digitalChannel.data[preference]?.categories || [];
        },
        setPersonalPromotionChannelPreference: (state, action) => {
            state.personalPromotionChannel.selectedPreference = action.payload.preference;
            const preference = PREFERENCE[action.payload.preference];
            state.personalPromotionChannel.chartSeries = state.personalPromotionChannel.data[preference]?.seriesData || [];
            state.personalPromotionChannel.categories = state.personalPromotionChannel.data[preference]?.categories || [];
        },
        setVendorAffinityChannel: (state, action) => {
            const channelName = action.payload.channel;
            const channelData = state.vendorAffinity.data[action.payload.channel] || [];
            state.vendorAffinity.selectedChannel = channelName;
                    
            const { series, vendors } = transformChartSeriesForSankey(channelName, channelData);
            state.vendorAffinity.chartSeries = series;
            state.vendorAffinity.vendors = vendors;
        }
    },
    extraReducers: (builder) => {
        builder
        // Years of Experience
            .addCase(getYearsOfExperience.fulfilled, (state, action) => {
                state.error = null;
                state.yearofExp.isLoading = false;
                const keys = Object.keys(action.payload || {});
                if(keys.includes('data')){
                    const chartSeriesData = [{
                        ...action.payload,
                        name: 'No. of HCPs'
                    }];
                    state.yearofExp.chartSeries = chartSeriesData || [];
                } else{
                    state.yearofExp.chartSeries = [];
                    
                }
                 
            })
            .addCase(getYearsOfExperience.pending, (state, action) => {
                state.yearofExp.isLoading = true;
                state.yearofExp.error = null;
            })
            .addCase(getYearsOfExperience.rejected, (state, action) => {
                state.yearofExp.isLoading = false;
                state.yearofExp.error = action.payload;
            })
        // Specialties
            .addCase(getSpecialtiesData.fulfilled, (state, action) => {
                state.error = null;
                state.specialties.isLoading = false;
                const keys = Object.keys(action.payload || {});
                if(keys.includes('data')){
                    state.specialties.chartSeries =createSpecialtiesData(action.payload) || [];
                } else{
                    state.specialties.chartSeries = [];
                }
            })
            .addCase(getSpecialtiesData.pending, (state, action) => {
                state.specialties.isLoading = true;
                state.specialties.error = null;
            })
            .addCase(getSpecialtiesData.rejected, (state, action) => {
                state.specialties.isLoading = false;
                state.specialties.error = action.payload;
            })
            // Distribution by Location
            .addCase(getTopStatesByIndividuals.fulfilled, (state, action) => {
                state.topStatesByIndividuals.error = null;
                state.topStatesByIndividuals.isLoading = false;
                if(action.payload?.code >= 200){
                } else{
                    state.topStatesByIndividuals.chartSeries = removeNotFound(action.payload || []);
                    state.topStatesByIndividuals.overAllIndividuals = action.payload?.reduce((acc, [, value]) => acc + value, 0);
                    state.topStatesByIndividuals.top15Total = action.payload?.slice(0, 15)?.reduce((acc, [, value]) => acc + value, 0);
                }
            })
            .addCase(getTopStatesByIndividuals.pending, (state, action) => {
                state.topStatesByIndividuals.isLoading = true;
                state.topStatesByIndividuals.error = null;
            })
            .addCase(getTopStatesByIndividuals.rejected, (state, action) => {
                state.topStatesByIndividuals.isLoading = false;
                state.topStatesByIndividuals.error = action.payload;
            })
            .addCase(getRegionCount.fulfilled, (state, action) => {
                state.topStatesByIndividuals.error = null;
                state.topStatesByIndividuals.isLoading = false;
                if(action.payload?.code >= 200){
                } else{
                    state.topStatesByIndividuals.mapSeries = createMapSeries(action.payload || []);
                }
            })
            .addCase(getRegionCount.pending, (state, action) => {
                state.topStatesByIndividuals.isLoading = true;
                state.topStatesByIndividuals.error = null;
            })
            .addCase(getRegionCount.rejected, (state, action) => {
                state.topStatesByIndividuals.isLoading = false;
                state.topStatesByIndividuals.error = action.payload;
            })
            /* Digital Vs Non Digital Affinity */ 
            .addCase(getDigitalVsNonDigitalAffinity.fulfilled, (state, action) => {
                state.digitalVsNonDigital.error = null;
                state.digitalVsNonDigital.isLoading = false;
                if(action.payload?.code >= 200){
                } else{
                    state.digitalVsNonDigital.chartSeries = createDigitalVsNonDigitalData(action.payload || []);
                }    
                 
            })
            .addCase(getDigitalVsNonDigitalAffinity.pending, (state, action) => {
                state.digitalVsNonDigital.isLoading = true;
                state.digitalVsNonDigital.error = null;
            })
            .addCase(getDigitalVsNonDigitalAffinity.rejected, (state, action) => {
                state.digitalVsNonDigital.isLoading = false;
                state.digitalVsNonDigital.error = action.payload;
            })
            // Affinity By Segment
            .addCase(getAffinityBySegment.fulfilled, (state, action) => {
                state.affinityBySegment.error = null;
                state.affinityBySegment.isLoading = false;
                if(action.payload?.code >= 200){
                } else{
                    state.affinityBySegment.digitalAffinityChartData = createAffinityBySegmentData(action.payload?.DG_score || []);
                    state.affinityBySegment.PeerToPeerAffinityChartData = createAffinityBySegmentData(action.payload?.PP_score || []);
                    state.affinityBySegment.PersonalPromotionAffinityChartData = createAffinityBySegmentData(action.payload?.PI_score || []);
                }    
                 
            })
            .addCase(getAffinityBySegment.pending, (state, action) => {
                state.affinityBySegment.isLoading = true;
                state.affinityBySegment.error = null;
            })
            .addCase(getAffinityBySegment.rejected, (state, action) => {
                state.affinityBySegment.isLoading = false;
                state.affinityBySegment.error = action.payload;
            })
        /* Personal Promotion Channel Affinity */ 
            .addCase(getPersonalPromotionChannelAffinity.fulfilled, (state, action) => {
                state.personalPromotionChannel.error = null;
                state.personalPromotionChannel.isLoading = false;
                if(action.payload?.code >= 200){
                } else{
                    const result = generateAffinityChartSeries(action.payload[0], false, canShowCounts(action));
                    state.personalPromotionChannel.data = result;
                    const preference = PREFERENCE[state.personalPromotionChannel.selectedPreference];
                    state.personalPromotionChannel.chartSeries = result[preference]?.seriesData || [];
                    state.personalPromotionChannel.categories = result[preference]?.categories || [];
                }    
                 
            })
            .addCase(getPersonalPromotionChannelAffinity.pending, (state, action) => {
                state.personalPromotionChannel.isLoading = true;
                state.personalPromotionChannel.error = null;
            })
            .addCase(getPersonalPromotionChannelAffinity.rejected, (state, action) => {
                state.personalPromotionChannel.isLoading = false;
                state.personalPromotionChannel.error = action.payload;
            })
            // Peer To Peer
            .addCase(getPeerPeerAffinityData.fulfilled, (state, action) => {
                state.peerToPeer.error = null;
                state.peerToPeer.isLoading = false;
                if(action.payload?.code >= 200){
                    return;
                } else{
                    state.peerToPeer.virtualChannel = createPeerToPeerAffinityData(action.payload?.data?.filter(item => item?.channel === 'Virtual') || [], true, canShowCounts(action));
                    state.peerToPeer.inPersonChannel = createPeerToPeerAffinityData(action.payload?.data?.filter(item => item?.channel === 'InPerson') || [], true, canShowCounts(action));
                    state.peerToPeer.peerToPeerChannel = createPeerToPeerAffinityData(action.payload?.pp_compare_affinity || [], false, canShowCounts(action));
                }   
                 
            })
            .addCase(getPeerPeerAffinityData.pending, (state, action) => {
                state.peerToPeer.isLoading = true;
                state.peerToPeer.error = null;
            })
            .addCase(getPeerPeerAffinityData.rejected, (state, action) => {
                state.peerToPeer.isLoading = false;
                state.peerToPeer.error = action.payload;
            })
            // Distribution by Years of Experience
            .addCase(getDistributionExperience.fulfilled, (state, action) => {
                state.distributionByYearsofExp.error = null;
                state.distributionByYearsofExp.isLoading = false;
                const keys = Object.keys(action.payload || {});
                if(keys.includes('data')){
                    state.distributionByYearsofExp.chartSeries =createDistributionYearExp(action.payload || []);
                } else{
                    state.distributionByYearsofExp.chartSeries = [];
                }  
                 
            })
            .addCase(getDistributionExperience.pending, (state, action) => {
                state.distributionByYearsofExp.isLoading = true;
                state.distributionByYearsofExp.error = null;
            })
            .addCase(getDistributionExperience.rejected, (state, action) => {
                state.distributionByYearsofExp.isLoading = false;
                state.distributionByYearsofExp.error = action.payload;
            })
            // Topic
            .addCase(getDynamicTopicCounts.fulfilled, (state, action) => {
                state.topic.error = null;
                state.topic.isLoading = false;
                if(action.payload?.code >= 200){
                } else{
                    state.topic.chartSeries = createTopicData(action.payload?.data || [], canShowCounts(action));
                    state.topic.topRatedCount = action.payload.top_rated_count?.toFixed(2) || 0;
                    state.topic.topRatedTopic = TOPICS[action.payload.top_rated_topic || ''];
                }    
                 
            })
            .addCase(getDynamicTopicCounts.pending, (state, action) => {
                state.topic.isLoading = true;
                state.topic.error = null;
            })
            .addCase(getDynamicTopicCounts.rejected, (state, action) => {
                state.topic.isLoading = false;
                state.topic.error = action.payload;
            })
            // Content Type
            .addCase(getBrandedUnbrandedCounts.fulfilled, (state, action) => {
                state.contentType.error = null;
                state.contentType.isLoading = false;
                if(action.payload?.code >= 200){
                } else{
                    state.contentType.chartSeries = createContentTypeData(action.payload?.data || []);
                    state.contentType.branded = action.payload?.branded;
                    state.contentType.unbranded = action.payload?.unbranded;
                }    
                 
            })
            .addCase(getBrandedUnbrandedCounts.pending, (state, action) => {
                state.contentType.isLoading = true;
                state.contentType.error = null;
            })
            .addCase(getBrandedUnbrandedCounts.rejected, (state, action) => {
                state.contentType.isLoading = false;
                state.contentType.error = action.payload;
            })
            // Content Affinity
            .addCase(getContentAffinityKeywords.fulfilled, (state, action) => {
                state.contentAffinity.error = null;
                state.contentAffinity.isLoading = false;
                if(action.payload?.code >= 200){
                } else {
                    state.contentAffinity.data = action.payload;
                    state.contentAffinity.chartSeries = createTreeMapSeries(action.payload.keywords.keywords2, 'Keyword 2', false);
                    state.contentAffinity.totalKeywordsCount = action.payload.total_keywords2_count ?? 0;
                }    
                 
            })
            .addCase(getContentAffinityKeywords.pending, (state, action) => {
                state.contentAffinity.isLoading = true;
                state.contentAffinity.error = null;
            })
            .addCase(getContentAffinityKeywords.rejected, (state, action) => {
                state.contentAffinity.isLoading = false;
                state.contentAffinity.error = action.payload;
            })
            // Vendor Affinity
            .addCase(getVendorAffinity.fulfilled, (state, action) => {
                state.vendorAffinity.error = null;
                state.vendorAffinity.isLoading = false;
                if(action.payload?.code >= 200){
                } else{
                    const { categories, chartSeries } = createSankeyChartSeries(action.payload);
                    state.vendorAffinity.data = chartSeries;
                    state.vendorAffinity.categories = categories;
                    state.vendorAffinity.selectedChannel = categories[0];
                    const { series, vendors } = transformChartSeriesForSankey(categories[0], chartSeries[categories[0]]);
                    state.vendorAffinity.chartSeries = series;
                    state.vendorAffinity.vendors = vendors;
                    
                }
            })
            .addCase(getVendorAffinity.pending, (state, action) => {
                state.vendorAffinity.isLoading = true;
                state.vendorAffinity.error = null;
            })
            .addCase(getVendorAffinity.rejected, (state, action) => {
                state.vendorAffinity.isLoading = false;
                state.vendorAffinity.error = action.payload;
            })
            // Match rates
            .addCase(getTotalPhysicians.fulfilled, (state, action) => {
                state.error = null;
                state.isLoading = false;
                if(action.payload?.code >= 200){
                } else{
                    state.totalPhysicians = action.payload?.number_of_physicians || 0;
                    state.matchRate = action.payload?.match_rate || 0;
                }
            })
            .addCase(getTotalPhysicians.pending, (state, action) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(getTotalPhysicians.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload;
            })
            /* Digital Channel Affinity */ 
            .addCase(getDigitalChannelAffinity.fulfilled, (state, action) => {
                state.digitalChannel.error = null;
                state.digitalChannel.isLoading = false;
                if(action.payload?.code >= 200){
                } else{
                    const result = generateAffinityChartSeries(action.payload[0], true, canShowCounts(action));
                    state.digitalChannel.data = result;
                    const preference = PREFERENCE[state.digitalChannel.selectedPreference];
                    state.digitalChannel.chartSeries = result[preference]?.seriesData || [];
                    state.digitalChannel.categories = result[preference]?.categories || [];
                }    
                 
            })
            .addCase(getDigitalChannelAffinity.pending, (state, action) => {
                state.digitalChannel.isLoading = true;
                state.digitalChannel.error = null;
            })
            .addCase(getDigitalChannelAffinity.rejected, (state, action) => {
                state.digitalChannel.isLoading = false;
                state.digitalChannel.error = action.payload;
            });


    }
});

const createSpecialtiesData = (data) => {
    const chartSeries = [];
    if(data?.data?.length){
        for(const series of data.data){
            if(series?.y>0 && series?.x !== ''){
                chartSeries.push(series);
            }
        }
    }
    return [{
        ...data, 
        data: chartSeries,
        name: 'No. of HCPs'
    }];
};



export const { actions } = physicianTargetListAnalysisSlice;
export default physicianTargetListAnalysisSlice.reducer;
